import React, { HtmlHTMLAttributes, useCallback, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Icon } from '../../../Icon';
import { colors } from '../../../../styles/tokens';
import { useSidebar } from '../../../../hooks/sidebar';
import { isFeatureGranted } from '../../../../utils/isFeatureGranted';
import {
  Container,
  OptMenu,
  Submenu,
  SubmenuWrapper,
  OptSubmenu,
} from './styles';

interface menuProps extends HtmlHTMLAttributes<HTMLUListElement> {
  icon?: string;
  label: string;
  path?: string;
  // Paths de outro item do menu que começam com o mesmo prefixo deste
  // (ex.: "Fichas de treino" = /trainings, "Templates de treino" =
  // /trainings/templates) -- sem isso, os dois acendem juntos em
  // qualquer rota de /trainings/templates/*, porque `isPathActive` é
  // um match por prefixo. Mesma ideia do `mostSpecificSubPath` do
  // submenu, mas pros irmãos de topo, que não compartilham uma lista.
  excludePaths?: string[];
  submenu?: subMenuProps[];
  onClick?: any;
  requiredPermission?: string;
  userPermissions?: string[];
  requiredFeature?: string | string[];
  userFeatures?: Record<string, number | null>;
}

interface subMenuProps {
  label: string;
  path: string;
  onClick?: any;
  requiredPermission?: string;
  requiredFeature?: string | string[];
}

// Distinção de propósito (Pedro, 2026-08-22): permissão decide "o que
// ESSA pessoa pode fazer" -- some do menu quando falta, igual sempre
// foi. Feature decide "o que o PLANO da academia inclui" -- em vez de
// sumir, mostra cadeado (convite pra upgrade), já que é sobre o que a
// academia contratou, não sobre o cargo de quem está logado.

const isPathActive = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

const hasPermission = (
  requiredPermission: string | undefined,
  userPermissions: string[],
): boolean =>
  !requiredPermission || userPermissions.includes(requiredPermission);

// Array = "basta uma dessas features" -- usado por itens de menu cuja
// página por trás combina seções independentes (ex.: Relatórios, que
// mostra retenção OU relatórios avançados conforme o que a academia
// tem, cada seção com seu próprio gate -- ver advancedReports).
const hasFeature = (
  requiredFeature: string | string[] | undefined,
  userFeatures: Record<string, number | null>,
): boolean => {
  if (!requiredFeature) return true;

  const codes = Array.isArray(requiredFeature)
    ? requiredFeature
    : [requiredFeature];

  return codes.some(code => isFeatureGranted(userFeatures[code]));
};

const ElementMenu: React.FC<menuProps> = ({
  label,
  icon,
  path,
  excludePaths,
  submenu,
  onClick,
  requiredPermission,
  userPermissions = [],
  requiredFeature,
  userFeatures = {},
  ...rest
}) => {
  const { pathname } = useLocation();
  const { collapsed, expand } = useSidebar();

  // Entre irmãos de submenu cujo path é prefixo um do outro (ex.:
  // `/users` e `/users/permissions`), `isPathActive` sozinho marcaria os
  // dois como ativos pra uma rota como `/users/permissions` -- só o mais
  // específico (path mais longo que ainda casa) deve acender. Não afeta
  // o caso original que a checagem por prefixo existe pra cobrir: uma
  // rota de detalhe tipo `/users/:id` continua só casando com `/users`,
  // já que nenhum outro irmão do submenu tem prefixo mais longo que
  // também combine.
  const matchingSubPaths =
    submenu?.filter(sub => isPathActive(pathname, sub.path)) ?? [];
  const mostSpecificSubPath = matchingSubPaths.reduce(
    (longest, sub) => (sub.path.length > longest.length ? sub.path : longest),
    '',
  );

  const isSubmenuActive = matchingSubPaths.length > 0;
  const isExcluded =
    excludePaths?.some(exclude => isPathActive(pathname, exclude)) ?? false;
  const isActive = path
    ? isPathActive(pathname, path) && !isExcluded
    : isSubmenuActive;

  const [activeSubmenu, setActiveSubmenu] = useState(isSubmenuActive);

  const handleActiveMenu = useCallback(() => {
    if (collapsed) {
      expand();
      setActiveSubmenu(true);
      return;
    }

    setActiveSubmenu(active => !active);
  }, [collapsed, expand]);

  const visible = hasPermission(requiredPermission, userPermissions);
  const locked = visible && !hasFeature(requiredFeature, userFeatures);

  return visible ? (
    <Container onClick={onClick} {...rest}>
      <OptMenu
        onClick={handleActiveMenu}
        show={visible}
        active={isActive}
        collapsed={collapsed}
        submenuOpen={activeSubmenu && !collapsed}
      >
        {icon && <Icon name={icon} size={22} color={colors.onSurfaceVariant} />}
        <span className="label">{label}</span>
        {locked && (
          <Icon
            className="lock"
            name="lock"
            size={16}
            color={colors.onSurfaceVariant}
          />
        )}
        {!locked && !!submenu?.length && (
          <Icon
            className="chevron"
            name="expand_more"
            size={20}
            color={colors.onSurfaceVariant}
          />
        )}
      </OptMenu>
      {!!submenu?.length && (
        <SubmenuWrapper activeSubmenu={activeSubmenu && !collapsed}>
          <Submenu>
            {submenu.map((sub, i) => {
              const subVisible = hasPermission(
                sub.requiredPermission,
                userPermissions,
              );
              const subLocked =
                subVisible && !hasFeature(sub.requiredFeature, userFeatures);

              return (
                <OptSubmenu
                  key={i.toString()}
                  onClick={sub.onClick}
                  show={subVisible}
                  active={sub.path === mostSpecificSubPath}
                >
                  <span>{sub.label}</span>
                  {subLocked && (
                    <Icon
                      className="lock"
                      name="lock"
                      size={14}
                      color={colors.onSurfaceVariant}
                    />
                  )}
                </OptSubmenu>
              );
            })}
          </Submenu>
        </SubmenuWrapper>
      )}
    </Container>
  ) : (
    <></>
  );
};

export { ElementMenu };
